/**
 * AWS inventory — the "what are we running and what is it costing"
 * half of /admin/spend. Rendered under the Cost Explorer cards.
 *
 * Source: GET /api/admin/v2/finances/aws-inventory/ (30-min shared cache).
 * The server builds it in the background (the S3 walk lists ~50k
 * objects), so a 202 {building, stale} means "poll again"; the stale copy
 * is shown meanwhile.
 *
 * Every section is {ok, data} or {ok:false, error}: a missing IAM
 * permission on the instance role blanks one card, never the page.
 * "≈ / month" figures are list-price estimates; billed numbers are in
 * the cost history table, straight from Cost Explorer.
 */
import { useEffect, useState } from "react";
import {
  RefreshCw, AlertTriangle, Loader2, Server, Database, Film,
  HardDrive, Globe, History, Activity,
} from "lucide-react";
import { getAwsInventory } from "../../api/owner.api";

const POLL_MS = 5000;
const POLL_LIMIT = 48; // 4 minutes

function usd(n, dp = 2) {
  if (n == null) return "—";
  return `$${Number(n).toFixed(dp)}`;
}

function gb(n) {
  if (n == null) return "—";
  if (n === 0) return "–";
  if (n >= 1000) return `${(n / 1000).toFixed(2)} TB`;
  if (n >= 10) return `${n.toFixed(0)} GB`;
  return `${n.toFixed(2)} GB`;
}

function num(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-IN");
}

function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-IN", {
    month: "short", year: "2-digit", timeZone: "UTC",
  });
}

function sumValues(obj) {
  return Object.values(obj || {}).reduce((a, b) => a + (b || 0), 0);
}

const SECTION_LABELS = {
  costs: "Cost history",
  forecast: "Forecast",
  compute: "Servers",
  database_aws: "Database (RDS)",
  database_postgres: "Database contents",
  bbb: "Ball-by-ball data",
  buckets: "Buckets",
  videos: "Video library",
  edge: "CloudFront / Lambda / Redis",
};

export default function AwsInventory() {
  const [data, setData] = useState(null);
  const [building, setBuilding] = useState(true);
  const [error, setError] = useState(null);
  // Each change fires one request; `poll` counts consecutive 202s.
  const [request, setRequest] = useState({ refresh: false, poll: 0 });

  useEffect(() => {
    let cancelled = false;
    let timer;
    getAwsInventory({ refresh: request.refresh })
      .then((res) => {
        if (cancelled) return;
        if (res.status === 202) {
          if (res.data?.stale) setData(res.data.stale);
          if (request.poll < POLL_LIMIT) {
            timer = setTimeout(
              () => setRequest({ refresh: false, poll: request.poll + 1 }),
              POLL_MS,
            );
          } else {
            setBuilding(false);
            setError("Still building after 4 minutes — try Refresh again later.");
          }
          return;
        }
        setBuilding(false);
        setData(res.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setBuilding(false);
        setError(e?.response?.data?.detail || e.message);
      });
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [request]);

  function refresh() {
    setError(null);
    setBuilding(true);
    setRequest({ refresh: true, poll: 0 });
  }

  const failed = data
    ? Object.keys(SECTION_LABELS).filter((k) => data[k] && !data[k].ok)
    : [];

  return (
    <div className="space-y-4 pt-2">
      <header className="flex items-center justify-between flex-wrap gap-3 border-t border-border-default pt-5">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Activity className="w-5 h-5" /> What we run
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Servers, database, ball-by-ball data, buckets and videos for
            ZenCricketData and the analyser (they share one API and one
            database). The same account also hosts the solar site and the
            parked ZenSportsLive box — each row says which project it is.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.cache_age_sec != null && !building && (
            <span className="text-[11px] text-text-secondary">
              built {Math.round(data.cache_age_sec / 60)} min ago
            </span>
          )}
          {building && (
            <span className="text-[11px] text-text-secondary inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> collecting from AWS…
            </span>
          )}
          <button
            type="button"
            onClick={refresh}
            disabled={building}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-border-default hover:bg-bg-secondary disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" /> Refresh inventory
          </button>
        </div>
      </header>

      {error && <ErrorBox>{error}</ErrorBox>}

      {!data && building && (
        <div className="flex items-center gap-2 text-text-secondary text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Collecting the inventory — this takes up to a minute the first time.
        </div>
      )}

      {failed.length > 0 && (
        <ErrorBox>
          <div className="font-medium">
            {failed.map((k) => SECTION_LABELS[k]).join(", ")} could not be read.
          </div>
          <div className="mt-1 text-text-secondary">
            Usually a missing read permission on the server's instance role
            (CricketBackendF-S3Backup). It needs:{" "}
            <code className="text-[11px]">
              {(data.required_iam_actions || []).join(", ")}
            </code>
          </div>
        </ErrorBox>
      )}

      {data && (
        <>
          <CostHistory costs={data.costs} forecast={data.forecast} />
          <BallData bbb={data.bbb} pg={data.database_postgres} />
          <Videos videos={data.videos} />
          <Compute compute={data.compute} />
          <DatabaseAws db={data.database_aws} pg={data.database_postgres} />
          <Buckets buckets={data.buckets} />
          <Edge edge={data.edge} />
        </>
      )}
    </div>
  );
}

// ─── Sections ───────────────────────────────────────────────────────

function CostHistory({ costs, forecast }) {
  if (!costs?.ok) return null;
  const { months, services, totals } = costs.data;
  const max = Math.max(...months.map((m) => totals[m] || 0), 1);
  const current = months[months.length - 1];
  const remaining = forecast?.ok ? forecast.data.remaining_usd : null;
  const projected = remaining != null ? (totals[current] || 0) + remaining : null;

  return (
    <Card icon={History} title="Cost history (billed, USD)"
      note="From Cost Explorer. The Tax line is 18% GST on that month's own usage.">
      {projected != null && (
        <div className="px-4 pt-3 text-sm">
          {monthLabel(current)} so far <b className="tabular-nums">{usd(totals[current])}</b>
          {" "}+ forecast for the rest of the month{" "}
          <b className="tabular-nums">{usd(remaining)}</b> ≈{" "}
          <b className="tabular-nums">{usd(projected)}</b>
        </div>
      )}
      <div className="p-3 space-y-1.5">
        {months.map((m) => (
          <div key={m} className="flex items-center gap-3 text-sm">
            <div className="w-16 text-text-secondary tabular-nums">{monthLabel(m)}</div>
            <div className="flex-1 h-2.5 bg-bg-secondary rounded">
              <div
                className="h-2.5 rounded bg-button-primary"
                style={{ width: `${((totals[m] || 0) / max) * 100}%` }}
              />
            </div>
            <div className="w-20 text-right tabular-nums">{usd(totals[m])}</div>
          </div>
        ))}
      </div>
      <ScrollTable>
        <thead className="text-text-secondary text-[12px] bg-bg-secondary/40">
          <tr>
            <Th left>Service</Th>
            {months.map((m) => <Th key={m}>{monthLabel(m)}</Th>)}
          </tr>
        </thead>
        <tbody>
          {services.map((s) => (
            <tr key={s.service} className="border-t border-border-default">
              <Td left nowrap>{s.service}</Td>
              {months.map((m) => (
                <Td key={m} muted={!s.by_month[m]}>
                  {s.by_month[m] ? s.by_month[m].toFixed(2) : "–"}
                </Td>
              ))}
            </tr>
          ))}
          <tr className="border-t border-border-default font-medium">
            <Td left>Total</Td>
            {months.map((m) => <Td key={m}>{(totals[m] || 0).toFixed(2)}</Td>)}
          </tr>
        </tbody>
      </ScrollTable>
    </Card>
  );
}

function BallData({ bbb, pg }) {
  if (!bbb) return null;
  if (!bbb.ok) return <SectionError title="Ball-by-ball data" error={bbb.error} />;
  const d = bbb.data;
  const coded = (d.balls.USER || 0) + (d.balls.ORG || 0);
  const codedMatches = (d.matches.USER || 0) + (d.matches.ORG || 0);
  return (
    <Card icon={Database} title="Ball-by-ball data"
      note="Ingested = the SYSTEM archive the analyser reads. Coded = matches scored in the app by users / organisations.">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-3">
        <Kpi label="Balls — ingested" value={num(d.balls.SYSTEM || 0)} sub={`${num(d.matches.SYSTEM || 0)} matches`} />
        <Kpi label="Balls — coded" value={num(coded)} sub={`${num(codedMatches)} matches`} />
        <Kpi label="Competitions" value={num(sumValues(d.competitions))}
          sub={`${num(d.competitions.SYSTEM || 0)} ingested`} />
        <Kpi label="Database size" value={pg?.ok ? gb(pg.data.size_gb) : "—"}
          sub={`${num(d.players_system)} archive players`} />
      </div>
      {Object.keys(d.ball_videos_by_state || {}).length > 0 && (
        <div className="px-4 pb-2 text-sm text-text-secondary">
          Ball videos linked to balls:{" "}
          {Object.entries(d.ball_videos_by_state)
            .sort((a, b) => b[1] - a[1])
            .map(([state, n]) => `${num(n)} ${state || "unknown"}`)
            .join(" · ")}
        </div>
      )}
      <ScrollTable>
        <thead className="text-text-secondary text-[12px] bg-bg-secondary/40">
          <tr><Th left>Largest ingested competitions</Th><Th>Matches</Th><Th>Balls</Th></tr>
        </thead>
        <tbody>
          {d.top_competitions.map((c) => (
            <tr key={c.competition} className="border-t border-border-default">
              <Td left>{c.competition}</Td>
              <Td>{num(c.matches)}</Td>
              <Td>{num(c.balls)}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    </Card>
  );
}

const KIND_LABELS = {
  full_match: "Full-match videos",
  ball_clip: "Ball clips",
  workbench_original: "Clip Workbench originals",
  ingest_zip: "Ingest ZIPs",
  live_chunk: "Live-recording chunks",
  other: "Other (invoices, voice notes, …)",
};

const CLASS_LABELS = {
  STANDARD: "Standard",
  GLACIER_IR: "Glacier Instant Retrieval",
  DEEP_ARCHIVE: "Deep Archive",
  STANDARD_IA: "Standard-IA",
};

function Videos({ videos }) {
  if (!videos) return null;
  if (!videos.ok) return <SectionError title="Video library" error={videos.error} />;
  const v = videos.data;
  return (
    <Card icon={Film} title={`Video library — ${v.bucket}`}
      note={`${num(v.objects)} files, ${gb(v.total_gb)}. Full-match videos go to Deep Archive and clips to Glacier IR; Standard is the expensive class.`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
        <MiniTable
          head={["Storage class", "Files", "Size"]}
          rows={v.by_storage_class.map((r) => [CLASS_LABELS[r.key] || r.key, num(r.objects), gb(r.gb)])}
        />
        <MiniTable
          head={["What it is", "Files", "Size"]}
          rows={v.by_kind.map((r) => [KIND_LABELS[r.key] || r.key, num(r.objects), gb(r.gb)])}
        />
      </div>
      <ScrollTable>
        <thead className="text-text-secondary text-[12px] bg-bg-secondary/40">
          <tr>
            <Th left>Tournament</Th><Th>Matches</Th><Th>Full videos</Th>
            <Th>Full size</Th><Th>Clips</Th><Th>Clip size</Th>
            <Th>Originals / other</Th><Th>In Standard</Th>
          </tr>
        </thead>
        <tbody>
          {v.tournaments.map((t) => (
            <tr key={t.tournament} className="border-t border-border-default">
              <Td left nowrap>{t.tournament.replace(/_/g, " ")}</Td>
              <Td>{num(t.matches)}</Td>
              <Td>{num(t.full_match_videos)}</Td>
              <Td>{gb(t.full_match_gb)}</Td>
              <Td muted={!t.clips}>{num(t.clips)}</Td>
              <Td muted={!t.clips}>{gb(t.clips_gb)}</Td>
              <Td muted={!t.other_gb}>{gb(t.other_gb)}</Td>
              <Td muted={!t.standard_gb}>{gb(t.standard_gb)}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
      {v.legacy_prefixes.length > 0 && (
        <div className="px-4 py-2.5 text-[12px] text-text-secondary border-t border-border-default">
          Older top-level folders not tied to a tournament:{" "}
          {v.legacy_prefixes.map((r) => `${r.key}/ ${num(r.objects)} files, ${gb(r.gb)}`).join(" · ")}
        </div>
      )}
    </Card>
  );
}

function Compute({ compute }) {
  if (!compute) return null;
  if (!compute.ok) return <SectionError title="Servers" error={compute.error} />;
  const c = compute.data;
  return (
    <Card icon={Server} title="Servers (EC2)"
      note="CPU is the 7-day average / peak. ≈ / month is the on-demand list price.">
      <ScrollTable>
        <thead className="text-text-secondary text-[12px] bg-bg-secondary/40">
          <tr>
            <Th left>Name</Th><Th left>Project</Th><Th left>Type</Th><Th left>State</Th>
            <Th>CPU avg / peak</Th><Th>≈ / month</Th>
          </tr>
        </thead>
        <tbody>
          {c.instances.map((i) => (
            <tr key={i.id} className="border-t border-border-default">
              <Td left nowrap>
                {i.name || i.id}
                <div className="text-[11px] text-text-secondary">{i.id}{i.public_ip ? ` · ${i.public_ip}` : ""}</div>
              </Td>
              <Td left>{i.project}</Td>
              <Td left>{i.type}</Td>
              <Td left><StateDot state={i.state} /></Td>
              <Td>{i.cpu_7d ? `${i.cpu_7d.avg.toFixed(1)}% / ${i.cpu_7d.max.toFixed(0)}%` : "—"}</Td>
              <Td>{usd(i.est_month_usd)}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
        <MiniTable
          head={["Disk", "Attached to", "≈ / month"]}
          rows={c.volumes.map((v) => [`${v.size_gb} GB ${v.type}`, v.attached_to || "—", usd(v.est_month_usd)])}
        />
        <MiniTable
          head={["Public IP", "Attached to", "≈ / month"]}
          rows={c.elastic_ips.map((a) => [a.ip, a.attached_to || "unattached", usd(a.est_month_usd)])}
        />
      </div>
      <div className="px-4 pb-3 text-[12px] text-text-secondary">
        {num(c.ebs_snapshots.count)} disk snapshots (backups), {num(c.ebs_snapshots.total_source_gb)} GB of source volumes.
      </div>
    </Card>
  );
}

function DatabaseAws({ db, pg }) {
  if (!db) return null;
  if (!db.ok) return <SectionError title="Database (RDS)" error={db.error} />;
  const d = db.data;
  return (
    <Card icon={Database} title="Database (RDS)"
      note="One Postgres serves both ZenCricketData and the analyser.">
      {d.instances.map((r) => (
        <div key={r.id} className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-3">
          <Kpi label={r.id} value={r.class} sub={`${r.engine} · ${r.status}${r.multi_az ? " · multi-AZ" : " · single-AZ"}`} />
          <Kpi label="Disk" value={`${r.allocated_gb} GB ${r.storage_type}`}
            sub={r.free_storage_gb != null ? `${gb(r.free_storage_gb)} free` : null} />
          <Kpi label="CPU 7d avg / peak" value={r.cpu_7d ? `${r.cpu_7d.avg.toFixed(1)}% / ${r.cpu_7d.max.toFixed(0)}%` : "—"}
            sub={r.connections_7d ? `up to ${r.connections_7d.max.toFixed(0)} connections` : null} />
          <Kpi label="≈ / month" value={usd(r.est_month_usd)}
            sub={`${r.backup_retention_days}-day backups · ${gb(r.freeable_memory_gb)} RAM free`} />
        </div>
      ))}
      <div className="px-4 pb-2 text-[12px] text-text-secondary">
        Snapshots: {Object.entries(d.snapshots).map(([k, n]) => `${n} ${k}`).join(", ") || "none"}
      </div>
      {pg?.ok && (
        <ScrollTable>
          <thead className="text-text-secondary text-[12px] bg-bg-secondary/40">
            <tr><Th left>Largest tables</Th><Th>Rows</Th><Th>Size</Th></tr>
          </thead>
          <tbody>
            {pg.data.largest_tables.map((t) => (
              <tr key={t.table} className="border-t border-border-default">
                <Td left>{t.table}</Td>
                <Td>{num(t.rows)}</Td>
                <Td>{t.size_mb >= 1000 ? `${(t.size_mb / 1000).toFixed(2)} GB` : `${t.size_mb} MB`}</Td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      )}
    </Card>
  );
}

function Buckets({ buckets }) {
  if (!buckets) return null;
  if (!buckets.ok) return <SectionError title="Buckets" error={buckets.error} />;
  return (
    <Card icon={HardDrive} title="S3 buckets"
      note="Sizes from S3's daily CloudWatch metrics, so they trail by about a day.">
      <ScrollTable>
        <thead className="text-text-secondary text-[12px] bg-bg-secondary/40">
          <tr>
            <Th left>Bucket</Th><Th left>Project</Th><Th>Files</Th>
            <Th>Size</Th><Th left>By class</Th><Th>≈ / month</Th>
          </tr>
        </thead>
        <tbody>
          {buckets.data.buckets.map((b) => (
            <tr key={b.name} className="border-t border-border-default">
              <Td left nowrap>
                {b.name}
                {b.region !== "us-east-1" && (
                  <span className="ml-1 text-[11px] text-text-secondary">({b.region})</span>
                )}
              </Td>
              <Td left>{b.project}</Td>
              <Td>{num(b.objects)}</Td>
              <Td>{gb(b.total_gb)}</Td>
              <Td left>
                <span className="text-[12px] text-text-secondary">
                  {Object.entries(b.gb_by_class).map(([c, g]) => `${CLASS_LABELS[c] || c} ${gb(g)}`).join(" · ") || "—"}
                </span>
              </Td>
              <Td>{usd(b.est_month_usd)}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    </Card>
  );
}

function Edge({ edge }) {
  if (!edge) return null;
  if (!edge.ok) return <SectionError title="CloudFront / Lambda / Redis" error={edge.error} />;
  const e = edge.data;
  return (
    <Card icon={Globe} title="CloudFront, Lambda and Redis">
      <ScrollTable>
        <thead className="text-text-secondary text-[12px] bg-bg-secondary/40">
          <tr><Th left>Site</Th><Th left>Project</Th><Th left>Serves from</Th><Th left>Status</Th></tr>
        </thead>
        <tbody>
          {e.cloudfront.map((d) => (
            <tr key={d.id} className="border-t border-border-default">
              <Td left nowrap>{d.domain}<div className="text-[11px] text-text-secondary">{d.id}</div></Td>
              <Td left>{d.project}</Td>
              <Td left><span className="text-[12px] text-text-secondary break-all">{d.origin}</span></Td>
              <Td left>{d.enabled ? "on" : "off"}</Td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
        <MiniTable
          head={["Lambda function", "Runtime", "Updated"]}
          rows={e.lambda.map((f) => [f.name, f.runtime || "—", f.last_modified])}
        />
        <MiniTable
          head={["Redis (ElastiCache)", "Node", "Status"]}
          rows={e.elasticache.length
            ? e.elasticache.map((c) => [c.id, c.node_type, c.status])
            : [["Not created yet", "—", "—"]]}
        />
      </div>
    </Card>
  );
}

// ─── Building blocks ────────────────────────────────────────────────

function Card({ icon: Icon, title, note, children }) {
  return (
    <section className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
      <header className="px-4 py-2.5 border-b border-border-default text-sm font-medium flex items-center gap-2 flex-wrap">
        {Icon && <Icon className="w-4 h-4" />} {title}
        {note && <span className="text-[11px] text-text-secondary font-normal">{note}</span>}
      </header>
      {children}
    </section>
  );
}

function SectionError({ title, error }) {
  return (
    <Card title={title}>
      <div className="px-4 py-3 text-sm text-text-secondary">Couldn't read this: {error}</div>
    </Card>
  );
}

function ErrorBox({ children }) {
  return (
    <div className="flex items-start gap-2 p-3 rounded-md border border-red-500/40 bg-red-500/10 text-sm">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
      <div>{children}</div>
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-border-default p-3">
      <div className="text-[11px] uppercase tracking-wider text-text-secondary truncate">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-text-secondary">{sub}</div>}
    </div>
  );
}

function ScrollTable({ children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

function Th({ children, left }) {
  return (
    <th className={`px-3 py-2 font-medium whitespace-nowrap ${left ? "text-left" : "text-right"}`}>
      {children}
    </th>
  );
}

function Td({ children, left, muted, nowrap }) {
  return (
    <td className={`px-3 py-2 align-top ${left ? "text-left" : "text-right tabular-nums"} ${muted ? "text-text-secondary" : ""} ${nowrap ? "whitespace-nowrap" : ""}`}>
      {children}
    </td>
  );
}

function MiniTable({ head, rows }) {
  return (
    <div className="rounded-md border border-border-default overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-text-secondary text-[12px] bg-bg-secondary/40">
          <tr>{head.map((h, i) => <Th key={h} left={i === 0 || i === 1}>{h}</Th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border-default">
              {r.map((cell, j) => <Td key={j} left={j === 0 || j === 1}>{cell}</Td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StateDot({ state }) {
  const color = state === "running" ? "bg-green-500" : state === "stopped" ? "bg-gray-400" : "bg-amber-500";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${color}`} /> {state}
    </span>
  );
}
